import React, { useEffect, useMemo, useState } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, IconButton, Tooltip, Typography, Chip } from "@mui/material";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { Button, Modal } from "abs-ui-components";
import { GetLatestUnsatisfactoryVesselsExport } from "../../../../services/AdminService";
import CircularProgress from "@mui/material/CircularProgress";
import { convertLocaltoUTCFormat } from "../../../../services/DateFormatUtility";
import axiosHttp from "../../../../services/interceptor";
import "../BulkExport/BulkExport.scss";

const UnsatisfactoryCIIRatingExportList = ({ refresh }) => {
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const [errModelOpen, setErrModalOpen] = useState(false);
    const [errorDetails, setErrorDetails] = useState(null);
    const [exportListPagination, setExportListPagination] = useState({
        pageIndex: 0,
        pageSize: 25,
    });
    const [rowCount, setRowCount] = useState(0);

    useEffect(() => {
        setExportListPagination(prev => ({
            ...prev,
            pageIndex: 0
        }))
    }, [refresh]);

    useEffect(() => {
        fetchLatestUnsatisfactoryVesselsExport();
    }, [exportListPagination.pageIndex, exportListPagination.pageSize]);

    const fetchLatestUnsatisfactoryVesselsExport = async () => {
        try {
            setLoading(true);
            const requestPayload = {
                "globalFilter": "",
                "filters": [],
                "pagination": {
                    page: exportListPagination.pageIndex + 1,
                    pageSize: exportListPagination.pageSize,
                },
                "sortOption": {
                    "sortField": "",
                    "isAscending": false
                },
                "reportYear": 0
            };
            const response = await GetLatestUnsatisfactoryVesselsExport(requestPayload);
            setRowCount(response?.data?.pagination?.totalItems || 0);
            console.log("Latest unsatisfactory vessels exports response:", response);
            const data = response?.data?.data || [];
            const sortedData = [...data].sort((a, b) => new Date(b.requestedTime) - new Date(a.requestedTime));

            setTableData(sortedData);
        } catch (error) {
            console.error("Error fetching unsatisfactory vessels exports", error);
            setTableData([]);
        }
        setLoading(false);
    };

    const handleDownload = async (originalData) => {
        try {
            setDownloadingId(originalData.id);
            const response = await axiosHttp.get(`api/Dashboard/DownloadUnsatisfactoryCii/${originalData.id}`,
                {
                    responseType: 'blob'
                }
            );

            const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `Unsatisfactory_CII_Export - ${(originalData.isAllVessels ? "all vessels" : `RY${originalData.reportingYear}`)}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Download failed", error);
        } finally {
            setDownloadingId(null);
        }
    };

    const columns = useMemo(
        () => [
            {
                header: "Requested By",
                accessorKey: "requestBy",
            },
            {
                header: "Requested On",
                accessorKey: "requestedDate",
                Cell: ({ cell }) => convertLocaltoUTCFormat(new Date(cell.getValue())),
            },
            {
                header: "Scope",
                accessorKey: "isAllVessels",
                size: 150,
                Cell: ({ cell }) => cell.getValue() ? "All Vessels" : "Reporting Year",
            },
            {
                header: "Reporting Year",
                accessorKey: "reportingYear",
                size: 150,
            },
            {
                header: "Status",
                accessorKey: "status",
                Cell: ({ cell, row }) => {
                    const status = cell.getValue();

                    const statusConfig = {
                        Completed: {
                            bgColor: "#28a745",
                            color: "#fff",
                        },
                        "In Progress": {
                            bgColor: "#ff9800",
                            color: "#fff",
                        },
                        Failed: {
                            bgColor: "#d32f2f",
                            color: "#fff",
                        },
                    };

                    const statusKey = Object.keys(statusConfig).find((key) =>
                        status?.startsWith(key)
                    );

                    const config = statusConfig[statusKey] || {
                        bgColor: "#757575",
                        color: "#fff",
                    };

                    return (
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.75}
                        >
                            <Chip
                                label={status}
                                size="medium"
                                sx={{
                                    backgroundColor: config.bgColor,
                                    color: config.color,
                                    fontWeight: 500,
                                    borderRadius: "16px",
                                    minWidth: 60,
                                    justifyContent: "center",
                                    "& .MuiChip-label": {
                                        px: 1.5,
                                    },
                                }}
                            />

                            {status === "Failed" && (
                                <ErrorOutlineOutlinedIcon
                                    fontSize="small"
                                    color="error"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                        setErrorDetails(row.original);
                                        setErrModalOpen(true);
                                    }}
                                />
                            )}
                        </Box>
                    );
                },
                size: 150,
            },
            {
                header: "Completed On",
                accessorKey: "completedDate",
                Cell: ({ cell }) => cell.getValue() && convertLocaltoUTCFormat(new Date(cell.getValue())),
            },
        ],
        []
    );

    const table = useMaterialReactTable({
        columns,
        data: tableData,
        state: {
            isLoading: loading,
            pagination: exportListPagination,
        },
        enableRowActions: true,
        enablePagination: true,
        enableSorting: false,
        enableColumnFilters: false,
        enableColumnActions: false,
        enableTopToolbar: true,
        enableHiding: false,
        layoutMode: "grid",
        enableStickyHeader: true,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableGlobalFilter: false,
        manualPagination: true,
        rowCount,
        onPaginationChange: setExportListPagination,
        muiTableContainerProps: {
            sx: {
                maxHeight: "550px",
                overflowY: "auto",
            },
        },
        displayColumnDefOptions: {
            "mrt-row-actions": {
                header: "",
                size: 60,
            },
        },
        renderRowActions: ({ row }) => {
            const isCompleted = row.original.status?.toLowerCase().includes("completed");

            if (!isCompleted) return null;

            return (
                <Tooltip title="Download">
                    <IconButton size="small" onClick={() => handleDownload(row.original)}>
                        {downloadingId === row.original.id ? <CircularProgress size={18} /> : <DownloadIcon sx={{ color: "#0965b6" }} />}
                    </IconButton>
                </Tooltip>
            );
        },
        renderTopToolbarCustomActions: () => (
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Tooltip title="Refresh">
                    <IconButton onClick={() => {
                        setExportListPagination(prev => ({
                            ...prev,
                            pageIndex: 0
                        }))
                        fetchLatestUnsatisfactoryVesselsExport()
                    }}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>

                <Typography sx={{ fontSize: "13px", color: "#555" }}>
                    Click to refresh data
                </Typography>
            </Box>
        ),
        muiPaginationProps: {
            rowsPerPageOptions: [25, 50, 75, 100],
            showFirstButton: true,
            showLastButton: true,
        },
    });

    return (
        <Box>
            <MaterialReactTable table={table} />
            {
                errModelOpen && (
                    <Modal
                        isOpen={errModelOpen}
                        modalHeader
                        padding={"50px"}
                        showFooter
                        showCloseIcon={false}
                        onCloseClickListener={() => {
                            setErrModalOpen(false);
                            setErrorDetails(null);
                        }}
                        footer={
                            <div className="center">

                                <Button
                                    className="close-btn mx-5 my-3"
                                    onClick={() => {
                                        setErrModalOpen(false);
                                        setErrorDetails(null);
                                    }}
                                >
                                    Close
                                </Button>
                            </div>
                        }
                    >
                        <div className="error-modal-body">
                            <h4 className="fw-bold mb-4">Error Details</h4>

                            <div className="mb-4">
                                <span className="fw-bold">Error: </span>
                                <span>{errorDetails?.errorMessage || "Export failed because no vessels matched the selected criteria."}</span>
                            </div>

                            <div className="mb-4">
                                <span className="fw-bold">Time: </span>
                                <span>{errorDetails?.completedDate && convertLocaltoUTCFormat(new Date(errorDetails?.completedDate))}</span>
                            </div>

                            <div>
                                <span className="fw-bold">Suggestion: </span>
                                <span>
                                    {errorDetails?.suggestion ||
                                        "Review the selected filters and try again."}
                                </span>
                            </div>
                        </div>
                    </Modal>
                )
            }
        </Box>
    );
};

export default UnsatisfactoryCIIRatingExportList;
